import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, MoreVertical, Copy, ExternalLink, Trash2 } from 'lucide-react';
import WorkflowUtilityPanel from './WorkflowUtilityPanel';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

function normalizeWorkflow(workflow) {
  if (!workflow) return null;
  return {
    ...workflow,
    id: workflow.id || workflow.workflow_id,
  };
}

function formatEditedDate(value) {
  if (!value) return 'Edited: —';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Edited: —';
  return `Edited: ${date.toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

function WorkflowCard({ workflow, highlighted, onOpen, onDuplicate, onOpenNewTab, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const status = workflow.status === 'active' ? 'Published' : 'Draft';

  return (
    <div
      className={cn(
        'relative rounded-lg border p-3 transition-colors',
        highlighted ? 'border-sky-300 bg-sky-50/60' : 'border-slate-200 hover:border-slate-300',
      )}
    >
      <button type="button" onClick={() => onOpen(workflow)} className="w-full text-left">
        <p className="pr-8 font-medium text-slate-900">{workflow.name}</p>
        <p className="mt-1 text-xs text-slate-500">{formatEditedDate(workflow.updated_at)}</p>
      </button>
      <span
        className={cn(
          'absolute right-10 top-3 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          status === 'Published'
            ? 'bg-emerald-100 text-emerald-700'
            : 'border border-slate-300 text-slate-600',
        )}
      >
        {status}
      </span>
      <div className="absolute right-2 top-2">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Workflow actions"
        >
          <MoreVertical size={16} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => {
                setMenuOpen(false);
                onDuplicate(workflow);
              }}
            >
              <Copy size={14} /> Duplicate workflow
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => {
                setMenuOpen(false);
                onOpenNewTab(workflow);
              }}
            >
              <ExternalLink size={14} /> Open in new tab
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              onClick={() => {
                setMenuOpen(false);
                onDelete(workflow);
              }}
            >
              <Trash2 size={14} /> Delete workflow
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const WorkflowSwitcherPanel = ({
  apiBase,
  token,
  currentWorkflowId,
  currentWorkflow,
  builderPathPrefix = '/platform/automations/workflows',
  onClose,
  onNavigate,
  onCreate,
}) => {
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBase}/recent`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setWorkflows((res.data?.workflows || []).map(normalizeWorkflow).filter(Boolean));
        }
      } catch {
        if (!cancelled) setWorkflows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workflows;
    return workflows.filter((w) => w.name?.toLowerCase().includes(q));
  }, [workflows, query]);

  const current = useMemo(() => {
    if (currentWorkflow) {
      return normalizeWorkflow({
        id: currentWorkflowId,
        workflow_id: currentWorkflowId,
        name: currentWorkflow.name,
        status: currentWorkflow.status,
        updated_at: currentWorkflow.updated_at,
      });
    }
    return workflows.find((w) => w.id === currentWorkflowId || w.workflow_id === currentWorkflowId);
  }, [currentWorkflow, currentWorkflowId, workflows]);

  const recent = filtered.filter((w) => w.id !== currentWorkflowId);

  const handleDuplicate = async (workflow) => {
    try {
      const res = await axios.post(
        `${apiBase}/${workflow.id}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Workflow duplicated');
      if (res.data?.workflow?.id) onNavigate(res.data.workflow.id);
    } catch {
      toast.error('Failed to duplicate workflow');
    }
  };

  const handleDelete = async (workflow) => {
    if (!window.confirm(`Delete "${workflow.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${apiBase}/${workflow.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Workflow deleted');
      setWorkflows((prev) => prev.filter((w) => w.id !== workflow.id));
      if (workflow.id === currentWorkflowId) onNavigate('new');
    } catch {
      toast.error('Failed to delete workflow');
    }
  };

  const openNewTab = (workflow) => {
    window.open(`${builderPathPrefix}/${workflow.id}/builder`, '_blank', 'noopener');
  };

  return (
    <WorkflowUtilityPanel
      title="Workflow switcher"
      subtitle="Quickly switch or search workflows without leaving your build ever."
      onClose={onClose}
      testId="workflow-switcher-panel"
      headerActions={(
        <button
          type="button"
          onClick={onCreate}
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Create workflow"
        >
          <Plus size={16} />
        </button>
      )}
    >
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search Workflow name"
        className="mb-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
        data-testid="workflow-switcher-search"
      />

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Current workflow</p>
            {current ? (
              <WorkflowCard
                workflow={current}
                highlighted
                onOpen={() => onNavigate(current.id)}
                onDuplicate={handleDuplicate}
                onOpenNewTab={openNewTab}
                onDelete={handleDelete}
              />
            ) : (
              <p className="text-sm text-slate-500">No current workflow loaded.</p>
            )}
          </section>
          <section>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Recent workflows</p>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-500">No recent workflows found.</p>
            ) : (
              <div className="space-y-2">
                {recent.map((workflow) => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    onOpen={() => onNavigate(workflow.id)}
                    onDuplicate={handleDuplicate}
                    onOpenNewTab={openNewTab}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </WorkflowUtilityPanel>
  );
};

export default WorkflowSwitcherPanel;
